import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';

const CustomerInfoSection = ({ data }) => {
  const theme = useTheme();
  const { customer, poCreation, creditTracking } = data;

  const formatAddress = (street, city, state, zip, country) => {
    const parts = [];
    if (street) parts.push(street);
    
    const cityStateZip = [];
    if (city) cityStateZip.push(city);
    if (state) cityStateZip.push(state);
    if (zip) cityStateZip.push(zip);
    if (cityStateZip.length > 0) parts.push(cityStateZip.join(', '));
    
    if (country) parts.push(country);
    return parts.join('\n');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <Card sx={{ 
      mb: 3,
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 20px ${alpha(theme.palette.grey[500], 0.15)}`
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.info.main, 0.1)
          }}>
            <PersonIcon sx={{ fontSize: 28, color: 'info.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Customer Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Billing and shipping details
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Customer Details */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}>
              <Typography variant="subtitle2" color="primary.main" fontWeight={500} sx={{ mb: 2 }}>
                CUSTOMER DETAILS
              </Typography>
              
              {/* Name */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                <PersonIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.3 }}>
                    Name
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {customer?.name || poCreation?.customer_name || 'N/A'}
                  </Typography>
                </Box>
              </Box>

              {/* Company */}
              {(customer?.company || poCreation?.customer_company) && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                  <BusinessIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.3 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.3 }}>
                      Company
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {customer?.company || poCreation?.customer_company}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Phone */}
              {customer?.phone && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                  <PhoneIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.3 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.3 }}>
                      Phone
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {customer.phone}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Email */}
              {customer?.email && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <EmailIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.3 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.3 }}>
                      Email
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {customer.email}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Billing Address */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: alpha(theme.palette.success.main, 0.03),
              border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
            }}>
              <Typography variant="subtitle2" color="success.main" fontWeight={500} sx={{ mb: 2 }}>
                BILLING ADDRESS
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <LocationIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.3 }} />
                <Box>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                    {formatAddress(
                      poCreation?.billing_street || customer?.street,
                      poCreation?.billing_city || customer?.city,
                      poCreation?.billing_state || customer?.state,
                      poCreation?.billing_zip || customer?.zip_code,
                      poCreation?.billing_country || customer?.country
                    ) || 'No billing address provided'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Shipping Address */}
          {(poCreation?.shipping_street || poCreation?.shipping_city || poCreation?.shipping_state) && (
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: alpha(theme.palette.warning.main, 0.03),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`
              }}>
                <Typography variant="subtitle2" color="warning.main" fontWeight={500} sx={{ mb: 2 }}>
                  SHIPPING ADDRESS
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <LocationIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.3 }} />
                  <Box>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                      {formatAddress(
                        poCreation?.shipping_street,
                        poCreation?.shipping_city,
                        poCreation?.shipping_state,
                        poCreation?.shipping_zip,
                        poCreation?.shipping_country
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Credit Information */}
          {creditTracking && (
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: alpha(theme.palette.info.main, 0.03),
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
              }}>
                <Typography variant="subtitle2" color="info.main" fontWeight={500} sx={{ mb: 2 }}>
                  CREDIT INFORMATION
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                  <CreditCardIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.3 }} />
                  <Box sx={{ flex: 1 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.3 }}>
                          Credit Limit
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(creditTracking.customer_credit_limit)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.3 }}>
                          Available
                        </Typography>
                        <Typography variant="body2" fontWeight={500} color={creditTracking.credit_available > 0 ? 'success.main' : 'error.main'}>
                          {formatCurrency(creditTracking.credit_available)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.3 }}>
                          Outstanding
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(creditTracking.current_outstanding_balance)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', textTransform: 'uppercase', mb: 0.3 }}>
                          Status
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight={500}
                          color={creditTracking.check_passed ? 'success.main' : 'error.main'}
                        >
                          {creditTracking.check_passed ? 'Approved' : 'Exceeded'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CustomerInfoSection;